/**
 * Runtime resolution for the TV scheme system.
 *
 * A premise stores a `SchemeBinding` (content + token overrides + knob
 * choices) in its config. Schemes never read that raw - they receive a
 * `ResolvedScheme`, where:
 *   - design tokens are the manifest defaults deep-merged with the operator's
 *     overrides (`resolveTokens`),
 *   - content has every `LocalizedString` flattened to a plain string for the
 *     active locale (`resolveContent`),
 *   - knobs are exposed as typed accessors that fall back to manifest defaults
 *     and tolerate stale/dangling values (`makeKnobAccessors`).
 *
 * `resolveScheme` ties these together and collects non-fatal `warnings` (the
 * TV ignores them; the CMS surfaces them).
 */
import { createContext, useContext } from "react";
import type { TvCatalogueHome } from "../../hooks/useTvCatalogue";
import type { TvPlayerChoice } from "@shared/types/foyer";
import type { TvLineup } from "../../hooks/useTvLineup";
import { resolveLocalized } from "@shared/i18n";
import { launchableApps } from "@shared/app-resolve";
import { detectPlatform, installedAppIds } from "../../lib/foyerBridge";
import type {
  CustomQr,
  CustomVignette,
  ScreenAnchor,
  SchemeBinding,
  SchemeBranding,
  SchemeBackground,
  SchemeContent,
  SchemeImages,
  SchemeWifi,
  SchemeKnobValues,
  SchemeManifest,
  SchemeTokenOverrides,
  SchemeTokens,
  SchemeSlots,
  SlotFill,
  SlotType,
  SlotSize,
} from "@shared/types/foyer";
import { slotFills } from "@shared/schemeSlots";
import type { TvRoomOfferFeed } from "@shared/types/roomOffer";
import { hasPromotionsLicense } from "@shared/products";
import { hasCertifiedApp } from "@shared/app-merge";
import { offerIsSellable } from "@shared/roomOffers";

// ── Resolved content (LocalizedString → plain string for the active locale) ──

export interface ResolvedApp {
  id: string;
  name: string;
  color: string;
  text: string;
  wordmark: string;
  letter: string;
  tag?: string;
  logoUrl?: string;
  enabled?: boolean;
  /** Per-OS launch identifiers, carried from the global catalogue.
   *  `iosScheme` is the Apple iOS / tvOS custom URL scheme (e.g. "nflx://"). */
  androidSlug?: string;
  tizenId?: string;
  webosId?: string;
  iosScheme?: string;
  /** https web address - the fallback when this unit has no native id. */
  webUrl?: string;
  /**
   * This app's placement and artwork are fixed by a vendor certification
   * (NFLX-4 / NFLX-5). Set by `withGlobalApps` and only where the pin is
   * actually in force, so a template reads it directly rather than re-deriving
   * the rule. `resolveContent` spreads the app, so it survives untouched.
   */
  certified?: boolean;
}
export interface ResolvedStation {
  id: string;
  name: string;
  tag: string;
  freq: string;
  /** @deprecated template derives the tint via `stationHue(id)`. */
  hue?: number;
  nowPlaying?: string;
  listeners?: string;
  streamUrl?: string;
  /** Sveriges Radio channel id for live now-playing (optional). */
  metadataId?: string;
  /** Station logo (optional). Global-catalogue channels carry this. */
  logoUrl?: string;
}
export interface ResolvedInfoItem {
  label: string;
  value: string;
}
export interface ResolvedInfoSection {
  id: string;
  section: string;
  items: ResolvedInfoItem[];
  /** Embedded URL (e.g. Vilmer activity scheme). `{integrationId}` unresolved
   *  here; the scheme substitutes the room token at render. */
  embedUrl?: string;
}
export interface ResolvedAction {
  id: string;
  label: string;
  detail?: string;
  icon?: string;
}
export interface ResolvedActionList {
  id: string;
  title?: string;
  actions: ResolvedAction[];
}
export interface ResolvedScreenCopy {
  kicker?: string;
  headline?: string;
  subtitle?: string;
}
export interface ResolvedCopy {
  home?: ResolvedScreenCopy;
  apps?: ResolvedScreenCopy;
  info?: ResolvedScreenCopy;
  radio?: ResolvedScreenCopy;
  hotel?: ResolvedScreenCopy;
}
/** Operator slot fill with LocalizedStrings flattened for the active locale.
 *  `enabled` is normalised to a boolean (default true). Time-window fields are
 *  carried through verbatim - gate at render time with {@link slotActive}. */
export type ResolvedAnnouncementSlot = {
  type: "announcement";
  enabled: boolean;
  size: SlotSize;
  title?: string;
  body: string;
  imageUrl?: string;
  startAt?: string;
  endAt?: string;
};
export type ResolvedQrSlot = {
  type: "qr";
  enabled: boolean;
  size: SlotSize;
  url: string;
  label?: string;
  startAt?: string;
  endAt?: string;
};
/**
 * An offer fill, flattened. Only the operator's OWN words are resolved here -
 * the offer itself is looked up in the live feed at render time (`POS-4`), so
 * nothing about it can go stale inside a scheme binding.
 */
export type ResolvedOfferSlot = {
  type: "offer";
  enabled: boolean;
  size: SlotSize;
  offerId: string;
  /** Scarcity copy; may still carry a `{kvar}` token - filled at render. */
  headline?: string;
  startAt?: string;
  endAt?: string;
};
export type ResolvedSlotFill =
  | ResolvedAnnouncementSlot
  | ResolvedQrSlot
  | ResolvedOfferSlot;
/** slot id → resolved fill. */
/** slot id → the resolved fills it draws, in order, side by side. */
export type ResolvedSlots = Record<string, ResolvedSlotFill[]>;

/** A serving with every localized field flattened to the active locale. */
export interface ResolvedMenuMeal {
  id: string;
  label: string;
  body: string;
  note?: string;
}
export interface ResolvedMenuDay {
  date: string;
  meals: ResolvedMenuMeal[];
}
export interface ResolvedMenuWeek {
  year: number;
  week: number;
  days: ResolvedMenuDay[];
}
export interface ResolvedMenu {
  source: "manual" | "feed";
  /** True when a feed URL is configured, so the screen knows to go fetch. */
  hasFeed: boolean;
  weeks: ResolvedMenuWeek[];
}

/** One Custom item with its label + words flattened for the active locale. */
export interface ResolvedCustomItem {
  id: string;
  label: string;
  imageUrl?: string;
  vignette?: CustomVignette;
  text?: {
    body: string;
    placement: ScreenAnchor;
    fontSize: number;
    color: string;
  };
  qr?: CustomQr;
}
export interface ResolvedCustom {
  items: ResolvedCustomItem[];
}

export interface ResolvedContent {
  branding: SchemeBranding;
  apps: ResolvedApp[];
  stations: ResolvedStation[];
  infoSections: ResolvedInfoSection[];
  actionLists: ResolvedActionList[];
  copy: ResolvedCopy;
  images: SchemeImages;
  /** Operator slot fills, resolved for the active locale. */
  slots: ResolvedSlots;
  background?: SchemeBackground;
  wifi?: SchemeWifi;
  welcome?: { title: string; body: string };
  relatives?: { sections: ResolvedInfoSection[]; embedUrl?: string };
  instagram?: {
    handle: string;
    caption?: string;
    showQr?: boolean;
    display?: "feed" | "invite" | "both";
    feedCount?: number;
  };
  weather?: { units?: "c" | "f" };
  /** Matsedel, flattened to the active locale. The stored `weeks` survive even
   *  while `source` is "feed" - the TV prefers the live feed and falls back to
   *  these, so a broken feed shows last-known-good rather than nothing. */
  menu?: ResolvedMenu;
  /** VilMer connection state. The activities themselves are fetched per room
   *  at render time and never travel in the config. */
  vilmer?: { hasApi: boolean };
  /** The operator-designed screen, words flattened to the active locale. */
  custom?: ResolvedCustom;
  homeIcons?: Record<string, string>;
  homeCards?: Record<string, { title?: string; description?: string }>;
}

// ── Foyer's own streaming client: the two feeds it draws from (TV-3a) ───────
//
// These live HERE rather than in `@shared/types/foyer` on purpose. They are the
// television's own VIEW of a catalogue, not the catalogue - the same split
// `TvRoomOffer` makes against the stored `RoomOffer` - and `foyer.ts` describes
// what a premise STORES. Nothing about films and channels is stored in a scheme
// binding; both arrive from their own HTTP route on their own clock, exactly as
// `offers` does. When the route lands (a sibling ticket) it resolves into these
// shapes and `SchemeRouter` spreads them onto the resolved scheme.

/** One title in the room's catalogue, as the television needs it. */
/**
 * The room's library and line-up, as the TELEVISION renders them.
 *
 * These were once two placeholder shapes of their own - a "menu view" carrying
 * just enough to count tiles. That was a mistake worth recording: the screens
 * fetched the real feeds separately, so a room polled twice for the same
 * answer, and in the design preview (and anywhere else the router already held
 * the data) the SCREENS got nothing at all while the MENU knew the section
 * existed. A menu that can see content its own pages cannot is the exact shape
 * of "Kanaler opens an empty page".
 *
 * One type each, from the hooks that define them, handed down by
 * `SchemeRouter`. The count the content gate needs is a read over the same
 * object the page draws, so the two can no longer disagree.
 */

/** Typed knob reads. Always return a sane value even for unknown ids. */
export interface SchemeKnobAccessors {
  /** Section/feature visible? Unknown id → true (fail visible). */
  toggle(id: string): boolean;
  /** Chosen variant id. Unknown id → "". */
  variant(id: string): string;
  /** Operator-typed text (e.g. a screen's name). Blank → the manifest
   *  default, so a name is never empty. Unknown id → "". */
  text(id: string): string;
  /** Ordered, enabled-only id list. Unknown id → []. */
  order(id: string): string[];
  /** True when the knob is declared in the manifest (any type). */
  has(id: string): boolean;
  /** The ids an order knob DECLARES (manifest items, not the stored order).
   *  Unknown id → []. */
  orderItems(id: string): string[];
  /** An order knob's manifest DEFAULT list - where its ids start out, before
   *  any operator choice. Unknown id → []. */
  orderDefault(id: string): string[];
  raw: SchemeKnobValues;
}

export interface ResolvedScheme {
  context: SchemeContextValue;
  content: ResolvedContent;
  tokens: SchemeTokens;
  knobs: SchemeKnobAccessors;
  /**
   * The premise's live room offers, exactly as the TV read route sent them
   * (`POS-3`). Deliberately NOT part of `content`: offers are their own
   * collection behind their own request, not a block of the scheme binding, and
   * folding them in would have made every template's config fetch wait on them.
   *
   * Absent means "not asked, or not answered yet" — which is why the Offers tile
   * is hidden until the feed lands rather than appearing empty and then
   * disappearing. `SchemeRouter` spreads it in once `useRoomOffers` resolves.
   */
  offers?: TvRoomOfferFeed;
  /**
   * The room's film and series catalogue, for Foyer's own player (`TV-3a`).
   *
   * Same contract as {@link offers}, and for the same reason: it is a second
   * request on a different clock, so folding it into the scheme binding would
   * make every template's config fetch wait on a catalogue read.
   *
   * **`undefined` is load-bearing.** It means "not asked for, or still on the
   * wire" - never "empty" - and `screenContentCount` counts it as nothing, which
   * is what keeps the tile off the home screen until the answer is in. A tile
   * that appears a moment late is cheaper than one that appears and then
   * vanishes, and far cheaper than one that opens an empty shelf.
   */
  vod?: TvCatalogueHome;
  /** The room's live channel line-up (`TV-3a`). Same `undefined` contract as
   *  {@link vod}. */
  guide?: TvLineup;
  /** Non-fatal issues (stale knob ids, type mismatches). TV ignores. */
  warnings: string[];
}

/** Hotel-level metadata each scheme threads to its screens. */
export interface SchemeContextValue {
  hotelName: string;
  city?: string;
  /** Premise coordinates (decimal degrees) - drives the Weather component. */
  lat?: number;
  lng?: number;
  /** Premise slug - used by care features (photo slideshow) in preview mode. */
  premiseId?: string;
  roomId?: string;
  /** CMS live preview only: a REAL room whose integration wiring the preview
   *  borrows, because the preview itself mounts as a synthetic `"preview"`
   *  room that matches no row. Read ONLY by the VilMer lookup - never by
   *  anything that reads resident data (whiteboard, photos), where pointing a
   *  preview at a real room would surface PHI in the template editor. */
  previewRoomId?: string;
  /** Paired room's integration token, substituted into embed URLs
   *  (e.g. the Vilmer activity scheme). */
  integrationId?: string;
  /**
   * Which player the TV tile opens — `PremiseConfig.tvPlayer` (`TV-6`).
   *
   * 🚨 Carried on the context because `openTv` otherwise reads the DURABLE
   * CACHE, keyed by the pairing binding's premise. A CMS preview has no
   * binding, so it read `null`, fell through to the `norigin` default and
   * launched an Android package that does not exist on the web — the press did
   * nothing at all, on a premise explicitly set to the built-in player.
   *
   * The config the app is RENDERING with is the more truthful of the two: on a
   * bound television they agree, and in a preview it is the only one there is.
   */
  tvPlayer?: TvPlayerChoice;
  /**
   * This is a PREVIEW, not a television in a room.
   *
   * Read only to show an operator diagnostics a guest must never see — the
   * sentence that says which of four things to go and fix when a room resolves
   * to nothing. Never a branch in layout or content: a preview that renders
   * differently from a panel is a preview nobody can trust.
   */
  preview?: boolean;
  /** Active display locale (the language the content is rendered in). */
  locale?: string;
  /**
   * The premise's own default locale — the fallback when a `LocalizedString`
   * carries nothing in the active one.
   *
   * `resolveContent` already applies it to everything inside the binding, so
   * almost nothing needs this. Offers do: they arrive from their own route,
   * localized strings intact, and are flattened at render time (`POS-3`).
   */
  defaultLocale?: string;
  /** Locales the premise supports - what a language switcher offers. */
  supportedLocales?: string[];
  /** Switch the active display language at runtime. Provided by SchemeRouter
   *  (re-resolves the scheme in the new locale). Absent in static previews. */
  setLocale?: (locale: string) => void;
}

// ── Per-station tint ────────────────────────────────────────────────────────

/**
 * Stable per-station accent hue (0-359) derived from the station id. Lets the
 * templates tint each station without the operator or catalogue storing a hue
 * - the template owns the colour. Same id always yields the same hue.
 */
export function stationHue(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = (h * 31 + id.charCodeAt(i)) % 360;
  }
  return h;
}

// ── Tokens ──────────────────────────────────────────────────────────────────

/** Deep-merge sparse operator overrides over the scheme's default tokens. */
export function resolveTokens(
  defaults: SchemeTokens,
  overrides?: SchemeTokenOverrides
): SchemeTokens {
  if (!overrides) return defaults;
  return {
    palette: { ...defaults.palette, ...overrides.palette },
    fonts: { ...defaults.fonts, ...overrides.fonts },
    extras: { ...defaults.extras, ...overrides.extras },
  };
}

// ── Knobs ─────────────────────────────────────────────────────────────────

/**
 * `showOffers`/`showPromotions` are gated by the Promotions LICENSE before
 * anything else: the CMS Products page decides whether this property sells
 * anything at all, and the knob only ever decided what's shown once it does
 * (docs/memory/features/room-offers.md).
 *
 * `showOffers` stopped being independently switchable on 2026-08-17: buying
 * the product is what turns the Offers screen on, full stop - a hotel that
 * licenses Promotions gets the menu row, and nothing else decides that. Only
 * `showPromotions` (the home-screen banner) keeps a real per-premise choice,
 * and only once the license is held.
 */
const PROMOTIONS_LICENSED_KNOBS = new Set(["showOffers", "showPromotions"]);

/**
 * Knobs that stop being switchable while a certified app is ON (NFLX-4).
 *
 * Netflix requires either its tile on the front screen or "a shortcut to an
 * apps section on the front screen", and Foyer satisfies the second form - so
 * while Netflix is showing, `showApps` is not a preference, it is the half of
 * the arrangement Netflix reviewed. An operator hiding the Apps screen leaves
 * the property carrying Netflix with no approved way to reach it, silently.
 *
 * The escape hatch is the app's own switch: turn Netflix off in Content → Apps
 * and this releases, because the agreement asks that Netflix come first where
 * it is carried, not that a hotel carry it.
 *
 * Forced ON rather than hidden: the Screens board still lists the row, with a
 * note saying what requires it (`screensModel`), exactly as Offers does once
 * Promotions is held.
 */
const CERTIFIED_LOCKED_ON_KNOBS = new Set(["showApps"]);

function makeKnobAccessors(
  manifest: SchemeManifest,
  values: SchemeKnobValues | undefined,
  warnings: string[],
  /**
   * Defaults to licensed. A caller that never asked about licensing at all -
   * a design preview, or one of the many existing tests that predate this
   * gate and have nothing to do with it - gets the old behaviour unchanged.
   * The real TV runtime (`SchemeRouter`) always passes the premise's actual
   * `PremiseConfig.products`, so this default is never reached in production.
   */
  hasPromotions = true,
  /**
   * Is a certified app switched on here, fixing part of the launcher
   * arrangement?
   *
   * Defaults to FALSE - the opposite default to `hasPromotions` above, and
   * deliberately so. That one withholds something a customer paid for, so not
   * knowing must fail towards granting it. This one IMPOSES a constraint, so
   * not knowing must fail towards leaving the operator alone: a preview or a
   * test built by hand must not find a switch it cannot move.
   */
  appsCertified = false
): SchemeKnobAccessors {
  const byId = new Map(manifest.knobs.map((k) => [k.id, k]));
  const raw = values ?? {};
  return {
    raw,
    toggle(id) {
      const knob = byId.get(id);
      if (!knob || knob.type !== "toggle") {
        warnings.push(`toggle knob "${id}" not declared in manifest`);
        return true;
      }
      if (PROMOTIONS_LICENSED_KNOBS.has(id)) {
        if (!hasPromotions) return false;
        if (id === "showOffers") return true;
      }
      if (appsCertified && CERTIFIED_LOCKED_ON_KNOBS.has(id)) return true;
      const v = raw[id];
      return typeof v === "boolean" ? v : knob.default;
    },
    variant(id) {
      const knob = byId.get(id);
      if (!knob || knob.type !== "variant") {
        warnings.push(`variant knob "${id}" not declared in manifest`);
        return "";
      }
      const v = raw[id];
      if (typeof v === "string" && knob.options.some((o) => o.id === v)) {
        return v;
      }
      return knob.default;
    },
    text(id) {
      const knob = byId.get(id);
      if (!knob || knob.type !== "text") {
        warnings.push(`text knob "${id}" not declared in manifest`);
        return "";
      }
      const v = raw[id];
      // Same rule as the CMS's `textValue`: whitespace is not a name.
      return (typeof v === "string" ? v.trim() : "") || knob.default;
    },
    has(id) {
      return byId.has(id);
    },
    // A declaration query like `has` - it asks what the TEMPLATE offers, not
    // what the operator chose, so an absent/mistyped knob is not a warning.
    orderItems(id) {
      const knob = byId.get(id);
      return knob && knob.type === "order" ? knob.items.map((i) => i.id) : [];
    },
    orderDefault(id) {
      const knob = byId.get(id);
      return knob && knob.type === "order" ? knob.default : [];
    },
    order(id) {
      const knob = byId.get(id);
      if (!knob || knob.type !== "order") {
        warnings.push(`order knob "${id}" not declared in manifest`);
        return [];
      }
      // Keep only ids the manifest declares - drops anything stale. (Filtering
      // against live content ids comes in Phase 3, when schemes read knobs.)
      const declared = new Set(knob.items.map((i) => i.id));
      const stored = raw[id];
      const chosen = Array.isArray(stored) ? stored : knob.default;
      return chosen.filter((c) => declared.has(c));
    },
  };
}

// ── Content ─────────────────────────────────────────────────────────────────

export function resolveContent(
  content: SchemeContent,
  locale: string,
  defaultLocale: string
): ResolvedContent {
  const L = (v: Parameters<typeof resolveLocalized>[0]) =>
    resolveLocalized(v, locale, defaultLocale);
  return {
    branding: content.branding,
    images: content.images ?? {},
    slots: resolveSlots(content.slots, L),
    background: content.background,
    // Streaming-app aggregation: one resolved list. `launchableApps` drops any
    // catalogue entry this device has no way of opening (no native id for this
    // OS and no https fallback) so a tile can never lead to a dead end. On plain
    // web - the CMS live preview - nothing is dropped, so the operator still
    // sees the full grid they configured.
    apps: installableHere(launchableApps(content.apps, detectPlatform())).map(
      (a) => ({
        ...a,
        tag: a.tag == null ? undefined : L(a.tag),
      })
    ),
    stations: content.stations.map((s) => ({
      ...s,
      tag: L(s.tag),
      nowPlaying: s.nowPlaying == null ? undefined : L(s.nowPlaying),
    })),
    infoSections: content.infoSections.map((sec) => ({
      id: sec.id,
      section: L(sec.section),
      items: sec.items.map((it) => ({
        label: L(it.label),
        value: L(it.value),
      })),
      embedUrl: sec.embedUrl,
    })),
    actionLists: (content.actionLists ?? []).map((al) => ({
      id: al.id,
      title: al.title == null ? undefined : L(al.title),
      actions: al.actions.map((ac) => ({
        id: ac.id,
        label: L(ac.label),
        detail: ac.detail == null ? undefined : L(ac.detail),
        icon: ac.icon,
      })),
    })),
    copy: resolveCopy(content.copy, L),
    wifi: content.wifi,
    welcome: content.welcome
      ? { title: L(content.welcome.title), body: L(content.welcome.body) }
      : undefined,
    relatives: content.relatives
      ? {
          sections: content.relatives.sections.map((sec) => ({
            id: sec.id,
            section: L(sec.section),
            items: sec.items.map((it) => ({
              label: L(it.label),
              value: L(it.value),
            })),
            embedUrl: sec.embedUrl,
          })),
          embedUrl: content.relatives.embedUrl,
        }
      : undefined,
    instagram: content.instagram
      ? {
          handle: content.instagram.handle,
          caption: content.instagram.caption
            ? L(content.instagram.caption)
            : undefined,
          showQr: content.instagram.showQr,
          display: content.instagram.display,
          feedCount: content.instagram.feedCount,
        }
      : undefined,
    weather: content.weather,
    menu: content.menu
      ? {
          source: content.menu.source ?? "manual",
          hasFeed: !!content.menu.feed?.url?.trim(),
          weeks: (content.menu.weeks ?? []).map((w) => ({
            year: w.year,
            week: w.week,
            days: w.days.map((d) => ({
              date: d.date,
              meals: d.meals.map((m) => ({
                id: m.id,
                label: L(m.label),
                body: L(m.body),
                note: m.note == null ? undefined : L(m.note),
              })),
            })),
          })),
        }
      : undefined,
    // VilMer carries no localizable content of its own - the activities live
    // upstream. All the TV needs to know is whether the premise is wired up,
    // so it can tell "not connected" from "a quiet week".
    vilmer: content.vilmer
      ? { hasApi: !!content.vilmer.apiUrl?.trim() }
      : undefined,
    // The operator-designed screen. Only the words are localizable; the
    // picture, the vignette and the QR mean the same thing in every language.
    custom: content.custom
      ? {
          items: (content.custom.items ?? []).map((it) => ({
            id: it.id,
            label: L(it.label),
            imageUrl: it.imageUrl,
            vignette: it.vignette,
            text: it.text
              ? {
                  body: L(it.text.body),
                  placement: it.text.placement,
                  fontSize: it.text.fontSize,
                  color: it.text.color,
                }
              : undefined,
            qr: it.qr,
          })),
        }
      : undefined,
    homeIcons: content.homeIcons,
    homeCards: content.homeCards
      ? Object.fromEntries(
          Object.entries(content.homeCards).map(([k, v]) => [
            k,
            {
              title: v.title ? L(v.title) : undefined,
              description: v.description ? L(v.description) : undefined,
            },
          ])
        )
      : undefined,
  };
}

/** Flatten the operator's slot fills for the active locale. Unknown types are
 *  dropped (forward-compat: a newer CMS could store a type this TV build can't
 *  render yet). */
/**
 * Drop apps this television does not actually have installed.
 *
 * `launchableApps` answers "is there an id for this OS?", which is a question
 * about the CATALOGUE. This answers "is that application on this panel?", which
 * is a question about the HARDWARE, and only the second one decides whether a
 * tile can lead anywhere. Setting a `tizenId` in the CMS made a Netflix tile
 * appear on a QB24C signage display that carries 213 applications and no
 * consumer apps at all - a button with nothing behind it.
 *
 * Filters only on a POSITIVE answer. Off Tizen, before the panel has replied,
 * or after a failed read, `installedAppIds()` is null and every tile stays -
 * hiding a tile that would have worked is the worse error, and a race must
 * never be able to empty an operator's grid.
 *
 * Entries that resolve to the https `webUrl` fallback are kept regardless: they
 * do not need a local application, which is the whole point of that fallback.
 */
function installableHere<
  T extends { launch: { kind: string; id: string | null } },
>(apps: T[]): T[] {
  const installed = installedAppIds();
  if (!installed) return apps;
  return apps.filter(
    (a) =>
      a.launch.kind !== "native" || !a.launch.id || installed.has(a.launch.id)
  );
}

function resolveSlots(
  slots: SchemeSlots | undefined,
  L: (v: Parameters<typeof resolveLocalized>[0]) => string
): ResolvedSlots {
  if (!slots) return {};
  const out: ResolvedSlots = {};
  for (const id of Object.keys(slots)) {
    out[id] = slotFills(slots, id)
      .map((fill) => resolveOneFill(fill, L))
      .filter((x): x is ResolvedSlotFill => x !== undefined);
  }
  return out;
}

function resolveOneFill(
  fill: SlotFill,
  L: (v: Parameters<typeof resolveLocalized>[0]) => string
): ResolvedSlotFill | undefined {
  {
    const enabled = fill.enabled !== false;
    const size: SlotSize = fill.size ?? "medium";
    if (fill.type === "announcement") {
      return {
        type: "announcement",
        enabled,
        size,
        title: fill.title == null ? undefined : L(fill.title),
        body: L(fill.body),
        imageUrl: fill.imageUrl || undefined,
        startAt: fill.startAt,
        endAt: fill.endAt,
      };
    } else if (fill.type === "qr") {
      return {
        type: "qr",
        enabled,
        size,
        url: fill.url,
        label: fill.label == null ? undefined : L(fill.label),
        startAt: fill.startAt,
        endAt: fill.endAt,
      };
    } else if (fill.type === "offer") {
      return {
        type: "offer",
        enabled,
        size,
        offerId: fill.offerId,
        headline: fill.headline == null ? undefined : L(fill.headline),
        startAt: fill.startAt,
        endAt: fill.endAt,
      };
    }
  }
  return undefined;
}

/**
 * Whether a resolved slot should currently render: it must be enabled, carry
 * non-empty content, and (if a start/end window is set) fall inside it. `now`
 * is injected so callers re-evaluate on a clock tick. Bad ISO dates are treated
 * as "no gate" rather than hiding the slot.
 */
export function slotActive(
  slot: ResolvedSlotFill | undefined,
  now: Date = new Date()
): boolean {
  if (!slot || !slot.enabled) return false;
  if (slot.type === "announcement" && !slot.body.trim()) return false;
  if (slot.type === "qr" && !slot.url.trim()) return false;
  // An offer fill with no offer chosen is an empty slot. Whether the CHOSEN
  // offer is live is the feed's answer, not this function's - see
  // `resolveOfferBanner` in slots/offerSlot.ts.
  if (slot.type === "offer" && !slot.offerId.trim()) return false;
  const t = now.getTime();
  if (slot.startAt) {
    const s = Date.parse(slot.startAt);
    if (Number.isFinite(s) && t < s) return false;
  }
  if (slot.endAt) {
    const e = Date.parse(slot.endAt);
    if (Number.isFinite(e) && t > e) return false;
  }
  return true;
}

/** Default slot types a manifest slot accepts when not narrowed. */
export const ALL_SLOT_TYPES: SlotType[] = ["announcement", "qr", "offer"];

function resolveCopy(
  copy: SchemeContent["copy"],
  L: (v: Parameters<typeof resolveLocalized>[0]) => string
): ResolvedCopy {
  const screen = (c?: {
    kicker?: unknown;
    headline?: unknown;
    subtitle?: unknown;
  }) =>
    c
      ? {
          kicker: c.kicker == null ? undefined : L(c.kicker as never),
          headline: c.headline == null ? undefined : L(c.headline as never),
          subtitle: c.subtitle == null ? undefined : L(c.subtitle as never),
        }
      : undefined;
  if (!copy) return {};
  return {
    home: screen(copy.home),
    apps: screen(copy.apps),
    info: screen(copy.info),
    radio: screen(copy.radio),
    hotel: screen(copy.hotel),
  };
}

// ── Top-level resolve ─────────────────────────────────────────────────────

// ── React context ─────────────────────────────────────────────────────────
// Schemes have many nested presentational components that all need the resolved
// palette/content. Rather than thread props through every level, the root wraps
// its tree in <SchemeProvider> and each component reads via `useScheme()` (and
// a per-scheme palette adapter built on top of it).

const SchemeCtx = createContext<ResolvedScheme | null>(null);
export const SchemeProvider = SchemeCtx.Provider;

export function useScheme(): ResolvedScheme {
  const v = useContext(SchemeCtx);
  if (!v) throw new Error("useScheme must be used inside a SchemeProvider");
  return v;
}

/** Maps a home destination id to the toggle knob that shows/hides it. */
const HOME_TOGGLE: Record<string, string> = {
  apps: "showApps",
  info: "showInfo",
  radio: "showRadio",
  wifi: "showWifi",
  welcome: "showWelcome",
  relatives: "showRelatives",
  instagram: "showInstagram",
  weather: "showWeather",
  // Care-P2: family-photo slideshow (care scheme only, default off)
  photos: "showPhotos",
  // Matsedel - the weekly meal plan (care scheme only, default off)
  menu: "showMenu",
  // VilMer - the activity calendar (care scheme only, default off)
  vilmer: "showVilmer",
  // The operator-designed screen (every template, default off)
  custom: "showCustom",
  // Room offers - what the hotel sells through the television (hotel templates
  // only, default off). POS-3.
  offers: "showOffers",
  // Foyer's own streaming client (hotel templates only, default off). TV-3a.
  vod: "showVod",
  guide: "showGuide",
};

/**
 * Screen id → the `text` knob holding the premise's own name for it.
 *
 * The CMS mirror of this join is `titleKnobId` on the screen's toggle, resolved
 * once by `templateSubLabel`. This is the television's half, and it exists so
 * the four templates that draw their own tiles (the bespoke three plus the
 * launcher dock) resolve a name in ONE place - a second local copy is how one of
 * them starts saying "Custom" again after an operator renamed it.
 */
const SCREEN_TITLE_KNOB: Record<string, string> = {
  custom: "customTitle",
  offers: "offersTitle",
  vod: "vodTitle",
  guide: "guideTitle",
};

/**
 * What this premise CALLS a screen: the name they typed, else the template's own
 * word for it.
 *
 * `has()` before `text()` because `knobs.text()` warns for an id the manifest
 * never declared - a template that does not offer the screen must fall straight
 * back to its own label rather than collect a warning per render.
 */
export function screenTitle(
  scheme: ResolvedScheme,
  id: string,
  fallback: string
): string {
  const knob = SCREEN_TITLE_KNOB[id];
  return knob && scheme.knobs.has(knob) ? scheme.knobs.text(knob) : fallback;
}

/**
 * The name the operator actually TYPED for a screen, or "" when they typed
 * nothing.
 *
 * A different question from {@link screenTitle}, which always answers with
 * something: this one distinguishes "renamed" from "left at the template's
 * word", and only a screen with LOCALIZED copy of its own can use the
 * difference. Manifest defaults are English (the CMS is), so an unrenamed Offers
 * screen headed by `knobs.text("offersTitle")` says "Offers" over Swedish body
 * copy for a Swedish hotel; headed by this, it falls back to its own copy pack
 * and says "Erbjudanden". Home tiles keep using `screenTitle` - they have no
 * localized word to fall back to.
 */
export function screenTitleOverride(
  scheme: ResolvedScheme,
  id: string
): string {
  const knob = SCREEN_TITLE_KNOB[id];
  if (!knob || !scheme.knobs.has(knob)) return "";
  const stored = scheme.knobs.raw[knob];
  return typeof stored === "string" ? stored.trim() : "";
}

/**
 * Home destinations with NO operator toggle - their visibility is derived at
 * runtime instead.
 *
 * `videocall` (VC2-8): the premise calling policy, the resident's seat and
 * their own opt-out already decide this, and the server states the outcome in
 * one answer (`/call-directory` → `reason`). A manual "show the tile" knob on
 * top of that was a trap - policy on, knob off, no tile, nothing on the TV or
 * in the CMS explaining why. `visibleHomeSections` therefore lets `videocall`
 * through and `CareScheme` filters it on the server's answer.
 */
const DERIVED_HOME_SCREENS = ["videocall"];

/**
 * The auxiliary screens that are NOT primary menu tiles. They live *inside* the
 * Information screen as a sub-list (so the home menu stays TV / Apps / Radio /
 * Information). Anhöriga (relatives) is intentionally NOT here - in Care it's a
 * primary tile.
 *
 * This is the FALLBACK placement, used by every template that does not declare
 * an `infoOrder` knob (the launcher family, the bespoke hotel schemes). A
 * template that declares one lets the operator move screens between the two
 * zones instead - see {@link screenPlacement}.
 */
export const SECONDARY_SCREENS = [
  // Matsedel leads the list - of everything filed under Information it is the
  // one a resident opens daily.
  "menu",
  "wifi",
  "welcome",
  "instagram",
  "weather",
];

/**
 * Screens whose visibility is the OPERATOR'S switch alone - they show as soon
 * as they are turned on and say "nothing here yet" when empty, rather than
 * hiding until content exists. Deliberately a property of the screen, not of
 * where it currently sits: moving Matsedel onto the home screen must not
 * silently change whether an empty Matsedel appears.
 */
const TOGGLE_DRIVEN_SCREENS = new Set([
  ...SECONDARY_SCREENS,
  "relatives",
  // VilMer is deliberately NOT in SECONDARY_SCREENS: that list is the fallback
  // placement for templates with no `infoOrder` knob, i.e. the launcher family
  // and the bespoke hotel schemes, none of which can render it. Care declares
  // it through `infoOrder` instead. It IS toggle-driven, though - switched on
  // with no API URL yet, the screen must appear and say so.
  "vilmer",
]);

/** Per-screen content count (0 = nothing to show → screen hides). */
function screenContentCount(scheme: ResolvedScheme, id: string): number {
  const c = scheme.content;
  switch (id) {
    case "apps":
      return c.apps.length;
    case "radio":
      return c.stations.length;
    case "info":
      // Info hosts the secondary screens, so it's "non-empty" whenever it has
      // sections OR at least one enabled secondary screen lives under it.
      return c.infoSections.length + visibleSecondarySections(scheme).length;
    case "wifi":
      return c.wifi?.ssid ? 1 : 0;
    case "welcome":
      return c.welcome?.title || c.welcome?.body ? 1 : 0;
    case "relatives":
      return c.relatives?.sections.length || c.relatives?.embedUrl ? 1 : 0;
    case "instagram":
      return c.instagram?.handle ? 1 : 0;
    case "weather":
      // No content list - Weather needs only the premise coordinates.
      return scheme.context.lat != null && scheme.context.lng != null ? 1 : 0;
    case "menu":
      // Days carrying at least one serving, across every stored week. A
      // feed-backed menu counts as non-empty on the strength of the feed
      // alone - its content lives upstream, not here.
      return scheme.content.menu?.hasFeed
        ? 1
        : (scheme.content.menu?.weeks ?? []).reduce(
            (n, w) => n + w.days.filter((d) => d.meals.length > 0).length,
            0
          );
    case "custom":
      // An item with nothing on it paints a black rectangle, so it does not
      // count: the tile appears when there is something to look at. This is
      // the apps/radio rule (hide when empty), NOT the Matsedel one - an empty
      // Custom screen has no copy of its own to explain itself with.
      return (scheme.content.custom?.items ?? []).filter(
        (it) => it.imageUrl || it.text?.body.trim() || it.qr?.url.trim()
      ).length;
    case "offers":
      // SELLABLE offers only (order/booking) - `offerIsSellable`, the same
      // predicate `OffersScreen`'s rail filters through. A `promotion`-kind
      // row is display-only content for the home slot, never a menu card
      // (2026-08-17: Offers is the sell mechanics, Promotions is only ever
      // the home-screen display), so a catalogue holding nothing but
      // promotions must NOT keep this tile alive - that is exactly the "Offers
      // tile that opened an empty shop" failure `docs/room-offers.md` §9
      // names, just one filter deeper than before.
      //
      // The server has already dropped disabled and out-of-window rows
      // (POS-2), so beyond that one filter this is the answer - re-deriving
      // anything else here would invent a second, subtly different opinion
      // about what a guest may see.
      //
      // Absent (the feed has not answered, or was never asked for) counts as
      // zero: the apps/radio rule, not the Matsedel one. A tile that appears a
      // moment late is cheaper than one that appears and then vanishes.
      return (scheme.offers?.offers ?? []).filter((o) =>
        offerIsSellable(o.action)
      ).length;
    case "vod":
      // Titles the room may actually watch, summed across its rails - the
      // server has already applied the packages and the play gate, so the count
      // IS the answer and re-filtering here would invent a second opinion about
      // what a guest may see.
      //
      // The apps/radio rule, not the Matsedel one: a "Films & series" tile that
      // opened an empty shelf is a worse lie than a tile that arrives a beat
      // late, and this screen has no copy of its own to explain itself with.
      // Absent (never asked for, or still on the wire) counts as zero.
      return (scheme.vod?.rails ?? []).reduce((n, r) => n + r.items.length, 0);
    case "guide":
      // Channels, not programmes. A line-up with channels and a guide feed that
      // has not answered yet is still a screen worth opening - it says what the
      // room can watch - whereas no channels at all is nothing to draw.
      return scheme.guide?.channels.length ?? 0;
    case "vilmer":
      // Connection state, not content: the activities live upstream and are
      // fetched per room. Zero means "nothing wired up", which the screen
      // reports rather than hides (it is toggle-driven).
      return scheme.content.vilmer?.hasApi ? 1 : 0;
    default:
      return 1;
  }
}

/**
 * Is a screen visible at all? Catalogue-backed screens (apps/info/radio) are
 * content-gated - an empty list hides them. The configurable feature screens
 * (Wi-Fi/Welcome/Instagram/Weather + Anhöriga) are toggle-driven: the operator
 * opts in via Components and the screen shows once enabled, regardless of
 * content (so the toggle has an immediate, predictable effect). Weather is the
 * one exception - it can't render without the premise's coordinates.
 *
 * {@link DERIVED_HOME_SCREENS} have no toggle at all and pass through here -
 * the owning scheme applies its own runtime gate (videocall: the server's
 * call-directory answer).
 */
function screenVisible(scheme: ResolvedScheme, id: string): boolean {
  const toggle = HOME_TOGGLE[id];
  if (toggle) {
    // A screen whose toggle this manifest does not DECLARE belongs to some
    // other template - never show it. `knobs.toggle()` fails visible for an
    // unknown id (the right default for a knob that merely went missing), so
    // without this check every toggle-driven screen reads as "on" for a
    // template that has never heard of it: `visibleSecondarySections` handed
    // Atelier a Wi-Fi/Välkommen/Väder list it cannot render. Dormant until
    // now only because the bespoke hotel schemes never call it - the same
    // guard `visibleHomeSections` already applies to its `extras` list.
    if (!scheme.knobs.has(toggle)) return false;
    if (!scheme.knobs.toggle(toggle)) return false;
  }
  if (id === "weather") {
    return scheme.context.lat != null && scheme.context.lng != null;
  }
  // Toggle-driven feature screens - show when enabled, fill content after, so
  // the operator switching one on always gets an immediate visible effect
  // (Matsedel's own screen says "ingen meny inlagd" when a week is empty).
  if (TOGGLE_DRIVEN_SCREENS.has(id)) return true;
  return screenContentCount(scheme, id) > 0;
}

/**
 * Where every screen sits for THIS premise, before visibility is applied.
 *
 * Placement is MEMBERSHIP of the two order knobs: an id in `infoOrder` is
 * listed inside the Information screen, an id in `homeOrder` is a home tile,
 * and the array position is the order within its zone. Templates that declare
 * no `infoOrder` keep the old compile-time answer ({@link SECONDARY_SCREENS}),
 * so nothing about the launcher or the bespoke hotel schemes changes.
 *
 * Two rules earn their keep:
 *
 *  - **Information wins a tie.** An id in both stored arrays stays under
 *    Information. Stored knob values are sparse and long-lived - a premise that
 *    saved `homeOrder` before Matsedel was reclassified still has "menu" in it -
 *    and re-declaring an id in `homeOrder.items` must not resurrect those.
 *  - **A screen in neither array is placed by DECLARATION**, not dropped: the
 *    zone whose `items` list names it (Information first). That is what keeps a
 *    switch immediately effective for a feature added after the premise last
 *    saved its order.
 */
export function screenPlacement(scheme: ResolvedScheme): {
  home: string[];
  info: string[];
} {
  const hasInfoOrder = scheme.knobs.has("infoOrder");
  const info = hasInfoOrder
    ? scheme.knobs.order("infoOrder")
    : SECONDARY_SCREENS.slice();
  const inInfo = new Set(info);
  const home = scheme.knobs.order("homeOrder").filter((id) => !inInfo.has(id));

  // Anything enabled but absent from both resolved arrays. Each candidate is
  // guarded against the manifest so it can never leak into a template that has
  // no such screen: toggle-driven ones by has(), the derived ones by the
  // homeOrder items the template declares.
  const seen = new Set([...home, ...info]);
  const declaredInfo = new Set(
    hasInfoOrder ? scheme.knobs.orderItems("infoOrder") : SECONDARY_SCREENS
  );
  const declaredHome = new Set(scheme.knobs.orderItems("homeOrder"));
  // An id BOTH zones declare needs a tie-break here too: its manifest `default`
  // is the zone it was designed to start in (Anhöriga is a home tile that may
  // be pushed under Information, not the reverse), so that decides before the
  // bare declaration does. Must stay in step with the CMS's `resolveOrders`.
  const defaultInfo = new Set(
    hasInfoOrder ? scheme.knobs.orderDefault("infoOrder") : SECONDARY_SCREENS
  );
  const defaultHome = new Set(scheme.knobs.orderDefault("homeOrder"));
  for (const id of [...Object.keys(HOME_TOGGLE), ...DERIVED_HOME_SCREENS]) {
    if (seen.has(id)) continue;
    const toggle = HOME_TOGGLE[id];
    if (toggle ? !scheme.knobs.has(toggle) : !declaredHome.has(id)) continue;
    const zone =
      defaultInfo.has(id) || (declaredInfo.has(id) && !defaultHome.has(id))
        ? info
        : home;
    zone.push(id);
  }
  return { home, info };
}

/**
 * Where a screen's Back key goes: `"info"` when the premise lists it inside the
 * Information screen, `"home"` when it is a home tile. Follows the PLACEMENT,
 * never the screen's identity - a Matsedel promoted to the home screen whose
 * Back still dropped the resident into Information would be a trapdoor.
 */
export function screenBackTarget(
  scheme: ResolvedScheme,
  id: string
): "home" | "info" {
  return screenPlacement(scheme).info.includes(id) ? "info" : "home";
}

/**
 * The ordered PRIMARY home destinations (apps/info/radio/relatives…) for a
 * scheme: {@link screenPlacement}'s home zone, minus anything switched off or
 * content-empty. Hotel/TV is the fixed anchor and is handled separately by each
 * scheme.
 */
export function visibleHomeSections(scheme: ResolvedScheme): string[] {
  return screenPlacement(scheme).home.filter((id) => screenVisible(scheme, id));
}

/**
 * The enabled screens listed inside the Information screen, in the operator's
 * order.
 */
export function visibleSecondarySections(scheme: ResolvedScheme): string[] {
  return screenPlacement(scheme).info.filter((id) => screenVisible(scheme, id));
}

export function resolveScheme(args: {
  manifest: SchemeManifest;
  /** Stored binding; when absent, `fallbackContent` is used (demo path). */
  binding?: SchemeBinding;
  fallbackContent: SchemeContent;
  locale: string;
  defaultLocale: string;
  premiseId?: string;
  roomId?: string;
  previewRoomId?: string;
  integrationId?: string;
  supportedLocales?: string[];
  setLocale?: (locale: string) => void;
  lat?: number;
  lng?: number;
  /**
   * This premise's active `License.product` values (`PremiseConfig.products`).
   * `undefined` means the caller never asked - previews and most tests - and
   * is treated as licensed, so `showOffers`/`showPromotions` behave exactly
   * as before this gate existed. A real fetched config always carries this,
   * even as `[]` for a premise with no licenses at all - which is what
   * actually turns the gate on. See `PROMOTIONS_LICENSED_KNOBS`.
   */
  products?: string[];
  /** `PremiseConfig.tvPlayer` — which player the TV tile opens (`TV-6`). */
  tvPlayer?: TvPlayerChoice;
  /** A preview rather than a bound television. Diagnostics only. */
  preview?: boolean;
}): ResolvedScheme {
  const {
    manifest,
    binding,
    fallbackContent,
    locale,
    defaultLocale,
    premiseId,
    roomId,
    tvPlayer,
    preview,
    previewRoomId,
    integrationId,
    supportedLocales,
    setLocale,
    lat,
    lng,
    products,
  } = args;
  const warnings: string[] = [];
  const content = binding?.content ?? fallbackContent;
  const resolved = resolveContent(content, locale, defaultLocale);
  const hasPromotions =
    products === undefined ? true : hasPromotionsLicense(products);
  // Read from the MERGED app list, so the constraint and the thing it protects
  // cannot disagree: `withGlobalApps` sets `certified` on exactly the entries
  // that are enrolled AND switched on, which is the same condition this gate
  // needs. One source of truth, and it is the list the guest actually sees -
  // including the case where the device cannot launch the app at all, where
  // there is nothing to reach and so nothing to defend.
  const appsCertified = hasCertifiedApp(resolved.apps);
  return {
    context: {
      hotelName: content.branding.hotelName,
      city: content.branding.city,
      lat,
      lng,
      premiseId,
      roomId,
      previewRoomId,
      integrationId,
      locale,
      defaultLocale,
      tvPlayer,
      preview,
      supportedLocales: supportedLocales ?? [],
      setLocale,
    },
    content: resolved,
    tokens: resolveTokens(manifest.defaultTokens, binding?.tokens),
    knobs: makeKnobAccessors(
      manifest,
      binding?.knobs,
      warnings,
      hasPromotions,
      appsCertified
    ),
    warnings,
  };
}
